import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { OnboardingProgress, OnboardingFlow, UserRole, OnboardingContext as OnboardingContextType } from '../../types/onboarding';
import { ONBOARDING_FLOWS } from '../../data/onboardingData';

interface OnboardingState {
  currentFlow: OnboardingFlow | null;
  progress: OnboardingProgress | null;
  context: OnboardingContextType | null;
  isActive: boolean;
  showTooltips: boolean;
  completedFlows: string[];
}

type OnboardingAction =
  | { type: 'START_FLOW'; payload: { flowId: string; userId: string } }
  | { type: 'COMPLETE_STEP'; payload: { stepId: string } }
  | { type: 'SKIP_STEP'; payload: { stepId: string } }
  | { type: 'SET_PROGRESS'; payload: OnboardingProgress }
  | { type: 'SET_CONTEXT'; payload: OnboardingContextType }
  | { type: 'TOGGLE_TOOLTIPS'; payload: boolean }
  | { type: 'COMPLETE_FLOW' }
  | { type: 'RESET_FLOW' };

const initialState: OnboardingState = {
  currentFlow: null,
  progress: null,
  context: null,
  isActive: false,
  showTooltips: true,
  completedFlows: []
};

function onboardingReducer(state: OnboardingState, action: OnboardingAction): OnboardingState {
  switch (action.type) {
    case 'START_FLOW': {
      const flow = ONBOARDING_FLOWS.find(f => f.id === action.payload.flowId);
      if (!flow) return state;

      const progress: OnboardingProgress = {
        userId: action.payload.userId,
        flowId: action.payload.flowId,
        currentStep: 0,
        completedSteps: [],
        startedAt: new Date(),
        lastActiveAt: new Date(),
        isCompleted: false,
        skipCount: 0
      };

      return {
        ...state,
        currentFlow: flow,
        progress,
        isActive: true
      };
    }

    case 'COMPLETE_STEP': {
      if (!state.progress || !state.currentFlow) return state;

      const updatedProgress = {
        ...state.progress,
        completedSteps: [...state.progress.completedSteps, action.payload.stepId],
        currentStep: state.progress.currentStep + 1,
        lastActiveAt: new Date()
      };

      const isFlowCompleted = updatedProgress.currentStep >= state.currentFlow.steps.length;

      return {
        ...state,
        progress: {
          ...updatedProgress,
          isCompleted: isFlowCompleted,
          completedAt: isFlowCompleted ? new Date() : undefined
        },
        isActive: !isFlowCompleted,
        completedFlows: isFlowCompleted 
          ? [...state.completedFlows, state.currentFlow.id]
          : state.completedFlows
      };
    }

    case 'SKIP_STEP': {
      if (!state.progress || !state.currentFlow) return state;

      return {
        ...state,
        progress: {
          ...state.progress,
          currentStep: state.progress.currentStep + 1,
          skipCount: state.progress.skipCount + 1,
          lastActiveAt: new Date()
        }
      };
    }

    case 'SET_PROGRESS': {
      return {
        ...state,
        progress: action.payload
      };
    }

    case 'SET_CONTEXT': {
      return {
        ...state,
        context: action.payload
      };
    }

    case 'TOGGLE_TOOLTIPS': {
      return {
        ...state,
        showTooltips: action.payload
      };
    }

    case 'COMPLETE_FLOW': {
      if (!state.progress || !state.currentFlow) return state;

      return {
        ...state,
        progress: {
          ...state.progress,
          isCompleted: true,
          completedAt: new Date()
        },
        isActive: false,
        completedFlows: [...state.completedFlows, state.currentFlow.id]
      };
    }

    case 'RESET_FLOW': {
      return {
        ...state,
        currentFlow: null,
        progress: null,
        isActive: false
      };
    }

    default:
      return state;
  }
}

interface OnboardingContextValue {
  state: OnboardingState;
  startFlow: (flowId: string, userId: string) => void;
  completeStep: (stepId: string) => void;
  skipStep: (stepId: string) => void;
  completeFlow: () => void;
  resetFlow: () => void;
  setContext: (context: OnboardingContextType) => void;
  toggleTooltips: (show: boolean) => void;
  getFlowForRole: (role: UserRole) => OnboardingFlow | null;
  shouldShowOnboarding: (userId: string, role: UserRole) => boolean;
  getCurrentStep: () => { step: any; index: number } | null;
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

export function OnboardingProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(onboardingReducer, initialState);

  // Load saved progress from localStorage
  useEffect(() => {
    const savedProgress = localStorage.getItem('clearOnboardingProgress');
    if (savedProgress) {
      try {
        const progress = JSON.parse(savedProgress);
        dispatch({ type: 'SET_PROGRESS', payload: progress });
      } catch (error) {
        console.warn('Failed to load onboarding progress:', error);
      }
    }

    const savedCompletedFlows = localStorage.getItem('clearCompletedFlows');
    if (savedCompletedFlows) {
      try {
        const completedFlows = JSON.parse(savedCompletedFlows);
        // Update state with completed flows
      } catch (error) {
        console.warn('Failed to load completed flows:', error);
      }
    }
  }, []);

  // Save progress to localStorage
  useEffect(() => {
    if (state.progress) {
      localStorage.setItem('clearOnboardingProgress', JSON.stringify(state.progress));
    }
    if (state.completedFlows.length > 0) {
      localStorage.setItem('clearCompletedFlows', JSON.stringify(state.completedFlows));
    }
  }, [state.progress, state.completedFlows]);

  const startFlow = (flowId: string, userId: string) => {
    dispatch({ type: 'START_FLOW', payload: { flowId, userId } });
  };

  const completeStep = (stepId: string) => {
    dispatch({ type: 'COMPLETE_STEP', payload: { stepId } });
  };

  const skipStep = (stepId: string) => {
    dispatch({ type: 'SKIP_STEP', payload: { stepId } });
  };

  const completeFlow = () => {
    dispatch({ type: 'COMPLETE_FLOW' });
  };

  const resetFlow = () => {
    dispatch({ type: 'RESET_FLOW' });
  };

  const setContext = (context: OnboardingContextType) => {
    dispatch({ type: 'SET_CONTEXT', payload: context });
  };

  const toggleTooltips = (show: boolean) => {
    dispatch({ type: 'TOGGLE_TOOLTIPS', payload: show });
  };

  const getFlowForRole = (role: UserRole): OnboardingFlow | null => {
    return ONBOARDING_FLOWS.find(flow => flow.role.id === role.id) || null;
  };

  const shouldShowOnboarding = (userId: string, role: UserRole): boolean => {
    const flow = getFlowForRole(role);
    if (!flow) return false;
    
    return !state.completedFlows.includes(flow.id);
  };

  const getCurrentStep = () => {
    if (!state.currentFlow || !state.progress) return null;
    
    const step = state.currentFlow.steps[state.progress.currentStep];
    return step ? { step, index: state.progress.currentStep } : null;
  };

  const value: OnboardingContextValue = {
    state,
    startFlow,
    completeStep,
    skipStep,
    completeFlow,
    resetFlow,
    setContext,
    toggleTooltips,
    getFlowForRole,
    shouldShowOnboarding,
    getCurrentStep
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
}